import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react'
import { Snackbar, Alert, AlertColor, Button } from '@mui/material'

interface Notification {
  id: string
  message: string
  severity: AlertColor
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

interface NotificationContextType {
  showNotification: (message: string, severity?: AlertColor, duration?: number,action?: { label: string, onClick: () => void }) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const showNotification = useCallback((
    message: string,
    severity: AlertColor = 'info',
    duration: number = 6000,
    action?: { label: string, onClick: () => void }
  ) => {
    const id = Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
    const notification: Notification = {
      id,
      message,
      severity,
      duration,
      action,
    }

    setNotifications((prev) => [...prev, notification])

    // Auto remove notification after duration
    if (duration > 0) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id))
      }, duration)
    }
  }, [])

  const handleClose = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  const value: NotificationContextType = {
    showNotification,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration}
          onClose={() => handleClose(notification.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: 8 }}
        >
          <Alert
            onClose={() => handleClose(notification.id)}
            severity={notification.severity}
            sx={{ width: '100%', minWidth: 300 }}
            action={notification.action ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => {
                  notification.action?.onClick();
                  handleClose(notification.id);
                }}
              >
                {notification.action.label}
              </Button>
            ) : undefined}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}
